import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { FileUpload, type FileUploadSelectEvent } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import type { Nullable } from "primereact/ts-helpers";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Organization } from "../../../../api/infrastructure/organization.formatter";
import LogoPlaceholder from "../../../assets/images/logo-placeholder.svg";
import { showToast } from "../../../shared/global-tools/toast-system";

const MAX_FILE_SIZE = 2097152; // 2MB

interface OrganizationFormProps {
  submitBtnLabel: string;
  onSubmit(input: {
    name: string;
    acronym: string;
    ownerName: string;
    ownerEmail: string;
    phone: string;
    address?: string;
    logo?: File;
    description?: string;
  }): Promise<void>;
  initialValues?: Organization;
}

export default function OrganizationForm({
  submitBtnLabel,
  onSubmit,
  initialValues,
}: OrganizationFormProps) {
  const [name, setName] = useState("");
  const [acronym, setAcronym] = useState("");
  const [description, setDescription] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [phone, setPhone] = useState<Nullable<string>>(null);
  const [address, setAddress] = useState("");
  const [logoFile, setLogoFile] = useState<File>();
  const [logoPreview, setLogoPreview] = useState<string>();
  const fileUploadRef = useRef<FileUpload>(null);
  const wrapAsync = useWrapAsync();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = wrapAsync(async () => {
    if (!name || !acronym || !ownerName || !ownerEmail || !phone) {
      showToast("Veuillez remplir les champs obligatoires", { severity: "warn" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(ownerEmail)) {
      showToast("Veuillez entrer une adresse email valide", { severity: "warn" });
      return;
    }
    const phoneRegex = /^(\d{10})$/;
    if (!phoneRegex.test(phone)) {
      showToast("Veuillez entrer un numéro de téléphone valide", { severity: "warn" });
      return;
    }

    setSaving(true);
    try {
      const cleanedPhone = `229${phone}`;
      await onSubmit({
        name,
        acronym,
        ownerName,
        ownerEmail,
        phone: cleanedPhone,
        address,
        logo: logoFile,
        description,
      });
      showToast("Enregistrement de la structure réussie", { severity: "success" });
      navigate("/app/organizations");
    } catch (error) {
      showToast("Echec enregistrement de la structure", { severity: "error" });
    } finally {
      setSaving(false);
    }
  });

  const onLogoSelect = (event: FileUploadSelectEvent) => {
    const file = event.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onLogoRemove = () => {
    setLogoFile(undefined);
    setLogoPreview(undefined);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const onValidationFailTemplate = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      showToast("La taille du fichier ne doit pas dépasser 2MB", { severity: "warn" });
      return;
    }
  };

  useEffect(() => {
    if (!initialValues) return;
    setName(initialValues.name ?? "");
    setAcronym(initialValues.acronym ?? "");
    setOwnerName(initialValues.ownerName ?? "");
    setOwnerEmail(initialValues.ownerEmail ?? "");
    if (initialValues.phone) {
      const phoneMatch = initialValues.phone.match(/^(\d{3})(\d{10})$/);
      if (phoneMatch) {
        setPhone(phoneMatch[2]);
      } else {
        setPhone(initialValues.phone);
      }
    } else {
      setPhone(null);
    }
    setLogoPreview(initialValues.logo);
    setAddress(initialValues.address ?? "");
    setDescription(initialValues.description ?? "");
  }, [initialValues]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <h2 className="OrganizationCreatePage-title">Informations de la structure</h2>
      <p className="OrganizationCreatePage-subtitle">
        Remplissez les champs obligatoires marqués avec{" "}
        <span style={{ color: "red", fontWeight: "bold" }}>*</span>{" "}
      </p>
      <div className="OrganizationCreatePage-uploadLogo">
        <p className="OrganizationCreatePage-uploadLogoTitle">Logo de la structure</p>

        <div className="OrganizationCreatePage-logoUpload">
          {logoPreview ? (
            <div className="OrganizationCreatePage-logoPreview">
              <img
                src={logoPreview}
                alt="Logo preview"
                className="OrganizationCreatePage-logoImage"
                style={{ maxHeight: "50px", width: "auto", objectFit: "contain" }}
              />
              <Button className="Btn danger small" label="Supprimer" onClick={onLogoRemove} />
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <img src={LogoPlaceholder} alt="Logo placeholder" />
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                name="logo"
                accept="image/*"
                maxFileSize={MAX_FILE_SIZE}
                onSelect={onLogoSelect}
                chooseLabel="Ajouter un logo"
                className="OrganizationCreatePage-fileUpload"
                auto={false}
                customUpload={true}
                onValidationFail={onValidationFailTemplate}
              />
            </div>
          )}
          <p className="OrganizationCreatePage-uploadHint">
            PNG, JPG jusqu'à 2MB. Recommandé: 200x200px
          </p>
        </div>
      </div>{" "}
      <div className="OrganizationCreatePage-formRow">
        <label className="Field w-full">
          <span className="Field-label">
            Nom <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            className="Field-input w-full"
            required
            value={name}
            onChange={(ev) => setName(ev.target.value)}
          />
        </label>

        <label className="Field w-full">
          <span className="Field-label">
            Sigle <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            className="Field-input w-full"
            required
            value={acronym}
            onChange={(ev) => setAcronym(ev.target.value)}
          />
        </label>
      </div>
      <div className="OrganizationCreatePage-formRow">
        <label className="Field w-full">
          <span className="Field-label">
            Nom du responsable <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            className="Field-input w-full"
            required
            value={ownerName}
            onChange={(ev) => setOwnerName(ev.target.value)}
          />
        </label>

        <label className="Field w-full">
          <span className="Field-label">
            Email du responsable <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            type="email"
            className="Field-input w-full"
            required
            value={ownerEmail}
            onChange={(ev) => setOwnerEmail(ev.target.value)}
          />
        </label>
      </div>
      <div className="OrganizationCreatePage-formRow">
        <label className="Field w-full">
          <span className="Field-label">
            Téléphone <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </span>
          <InputText
            type="tel"
            placeholder="0166001223"
            className="Field-input w-full"
            required
            value={phone}
            onChange={(ev) => setPhone(ev.target.value)}
            maxLength={10}
            keyfilter="num"
          />
        </label>

        <label className="Field w-full">
          <span className="Field-label">Adresse</span>
          <InputText
            className="Field-input w-full"
            value={address}
            onChange={(ev) => setAddress(ev.target.value)}
          />
        </label>
      </div>
      <label className="Field w-full">
        <span className="Field-label">Description</span>
        <InputTextarea
          rows={3}
          className="Field-textarea w-full"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <div className="OrganizationCreatePage-footer">
        <Button
          className="Btn secondary outlined"
          label="Annuler"
          onClick={() => navigate("/app/organizations")}
        />
        <Button type="submit" className="Btn primary" label={submitBtnLabel} disabled={saving} />
      </div>
    </form>
  );
}
